"use client";
import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";
import { getFriendlyErrorMessage } from "@/lib/error-messages";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const data = await apiFetch<{ message: string }>("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setSuccess(data.message ?? "Reset link sent. Check your inbox.");
    } catch (err) {
      setError(getFriendlyErrorMessage(err, "We couldn't send the reset link. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link
            href="/"
            className="inline-flex items-center justify-center"
            data-testid="link-home"
          >
            <img
              src="https://www.injazalarab.org/_nuxt/img/logo.a19ce7d.png"
              alt="INJAZ Al-Arab Logo"
              className="h-10 object-contain"
              data-testid="img-logo"
            />
          </Link>
          <h1 className="mt-6 text-3xl font-bold" data-testid="text-forgot-title">
            Reset password
          </h1>
          <p className="mt-2 text-muted-foreground" data-testid="text-forgot-subtitle">
            Enter your email and we&apos;ll send you reset instructions.
          </p>
        </div>

        <Card className="p-6 rounded-2xl border-border/60 shadow-sm">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" data-testid="label-email">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSubmit()}
                data-testid="input-email"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600" data-testid="status-forgot-error">
                {error}
              </div>
            )}
            {success && (
              <div className="text-sm text-green-600" data-testid="status-forgot-success">
                {success}
              </div>
            )}

            <Button
              className="w-full h-12 rounded-full font-semibold"
              onClick={onSubmit}
              disabled={loading}
              data-testid="button-send-reset"
            >
              {loading ? "Sending..." : "Send reset link"}
            </Button>

            <p className="text-sm text-muted-foreground text-center">
              Remembered your password?{" "}
              <Link href="/login" className="text-primary hover:underline" data-testid="link-login">
                Back to login
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
